import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { LazyTooltip } from './Tooltip';
import type { MatchScoreBreakdown } from '../utils/data/calculate-job-match-score';

/** Pre-rendered strings for MatchDonut when used without IntlProvider (e.g. static SRP). */
export interface MatchDonutStaticStrings {
  ariaLabelTemplate: string;
  breakdownTitle: string;
  breakdownSalary: string;
  breakdownVacation: string;
  breakdownBenefits: string;
  breakdownRemoteness: string;
  /** Aria label for empty donut (no score / login to see). */
  loginPromptAria: string;
}

interface MatchDonutProps {
  /** 0-100 when known; null for static view when user not logged in / score not available. */
  score: number | null;
  size?: number;
  className?: string;
  breakdown?: MatchScoreBreakdown;
  /** When provided, no useIntl/FormattedMessage (for static SSR). Enables null score → empty donut with "?". */
  strings?: MatchDonutStaticStrings;
}

function getDonutColor(score: number): string {
  if (score >= 75) {
    const ratio = (score - 75) / 25;
    return `rgb(${Math.round(34 + (76 - 34) * (1 - ratio))}, ${Math.round(197 + (163 - 197) * (1 - ratio))}, ${Math.round(94 + (18 - 94) * (1 - ratio))})`;
  }
  if (score >= 50) {
    const ratio = (score - 50) / 25;
    return `rgb(${Math.round(234 + (34 - 234) * (1 - ratio))}, ${Math.round(179 + (197 - 179) * (1 - ratio))}, ${Math.round(8 + (94 - 8) * (1 - ratio))})`;
  }
  if (score >= 25) {
    const ratio = (score - 25) / 25;
    return `rgb(${Math.round(249 + (234 - 249) * (1 - ratio))}, ${Math.round(115 + (179 - 115) * (1 - ratio))}, ${Math.round(22 + (8 - 22) * (1 - ratio))})`;
  }
  const ratio = score / 25;
  return `rgb(${Math.round(239 + (249 - 239) * (1 - ratio))}, ${Math.round(68 + (115 - 68) * (1 - ratio))}, ${Math.round(68 + (22 - 68) * (1 - ratio))})`;
}

/** Renders donut without react-intl; used when strings are provided (static SRP). Handles null score as empty donut with "?". */
function MatchDonutWithStrings({
  score,
  size = 64,
  className = '',
  breakdown,
  strings,
}: MatchDonutProps & { strings: MatchDonutStaticStrings }): React.ReactElement {
  const strokeWidth = Math.max(4, size / 12);
  const radius = (size - strokeWidth) / 2;

  if (score === null || score === undefined || !Number.isFinite(score)) {
    const ariaLabel = strings.loginPromptAria ?? 'Log in to see your match score';
    return (
      <div
        className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
        role="img"
        aria-label={ariaLabel}
      >
        <span
          className="icon-mask transform -rotate-90 text-gray-300 dark:text-gray-600 flex-shrink-0"
          style={{
            width: size,
            height: size,
            ['--icon-url' as string]: 'url(/icons/match-donut-empty.svg)',
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ fontSize: `${size * 0.25}px` }}
        >
          <span className="font-bold text-gray-400 dark:text-gray-500">?</span>
        </div>
        {/* Lock icon to match MatchDonutWithLoginPrompt empty state */}
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center"
          style={{ fontSize: '8px' }}
        >
          <span className="text-white">🔒</span>
        </div>
      </div>
    );
  }

  const clampedScore = Math.max(0, Math.min(100, score));
  const circumference = 2 * Math.PI * radius;
  const percentage = clampedScore / 100;
  const dashOffset = circumference - percentage * circumference;
  const color = getDonutColor(clampedScore);
  const ariaLabel = strings.ariaLabelTemplate.replace('{score}', String(clampedScore));

  const breakdownTooltip = breakdown ? (
    <div className="text-sm">
      <div className="font-semibold mb-2">{strings.breakdownTitle}</div>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span>{strings.breakdownSalary}</span>
          <span className="font-medium">{breakdown.salary}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.salaryWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>{strings.breakdownVacation}</span>
          <span className="font-medium">{breakdown.vacation}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.vacationWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>{strings.breakdownBenefits}</span>
          <span className="font-medium">{breakdown.benefits}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.benefitsWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>{strings.breakdownRemoteness}</span>
          <span className="font-medium">{breakdown.remoteness}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.remotenessWeight * 100)}%)</span>
        </div>
      </div>
    </div>
  ) : undefined;

  const donutElement = (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="transform -rotate-90" aria-label={ariaLabel}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ fontSize: `${size * 0.25}px` }}
      >
        <span className="font-bold" style={{ color }}>
          {clampedScore}
        </span>
      </div>
    </div>
  );

  if (breakdownTooltip) {
    return (
      <LazyTooltip content={breakdownTooltip} position="top">
        {donutElement}
      </LazyTooltip>
    );
  }
  return donutElement;
}

/** Renders donut using react-intl (interactive tree). Call only when score is a number. */
function MatchDonutWithIntl({
  score,
  size = 64,
  className = '',
  breakdown,
}: MatchDonutProps & { score: number }): React.ReactElement {
  const intl = useIntl();
  const clampedScore = Math.max(0, Math.min(100, score));
  const strokeWidth = Math.max(4, size / 12);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = clampedScore / 100;
  const dashOffset = circumference - percentage * circumference;
  const color = getDonutColor(clampedScore);

  const breakdownTooltip = breakdown ? (
    <div className="text-sm">
      <div className="font-semibold mb-2">
        <FormattedMessage
          id="matchDonut.breakdown.title"
          defaultMessage="Match Score Breakdown"
          description="Title for match score breakdown tooltip"
        />
      </div>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span>
            <FormattedMessage id="matchDonut.breakdown.salary" defaultMessage="Salary" description="Salary label in breakdown" />
          </span>
          <span className="font-medium">{breakdown.salary}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.salaryWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>
            <FormattedMessage id="matchDonut.breakdown.vacation" defaultMessage="Vacation" description="Vacation label in breakdown" />
          </span>
          <span className="font-medium">{breakdown.vacation}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.vacationWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>
            <FormattedMessage id="matchDonut.breakdown.benefits" defaultMessage="Benefits" description="Benefits label in breakdown" />
          </span>
          <span className="font-medium">{breakdown.benefits}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.benefitsWeight * 100)}%)</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>
            <FormattedMessage id="matchDonut.breakdown.remoteness" defaultMessage="Remoteness" description="Remoteness label in breakdown" />
          </span>
          <span className="font-medium">{breakdown.remoteness}%</span>
          <span className="text-xs text-gray-500">({Math.round(breakdown.weights.remotenessWeight * 100)}%)</span>
        </div>
      </div>
    </div>
  ) : undefined;

  const donutElement = (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        aria-label={intl.formatMessage(
          { id: 'matchDonut.ariaLabel', defaultMessage: 'Match score: {score}%', description: 'Accessibility label for match donut chart' },
          { score: clampedScore }
        )}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center" style={{ fontSize: `${size * 0.25}px` }}>
        <span className="font-bold" style={{ color }}>
          {clampedScore}
        </span>
      </div>
    </div>
  );

  if (breakdownTooltip) {
    return (
      <LazyTooltip content={breakdownTooltip} position="top">
        {donutElement}
      </LazyTooltip>
    );
  }
  return donutElement;
}

/**
 * Match Donut Component
 * Shows a circular donut chart with a score from 0-100 in the center.
 * When strings are provided (static SRP), uses them and can show empty donut with "?" when score is null.
 */
export const MatchDonut: React.FC<MatchDonutProps> = (props) => {
  if (props.strings) {
    return <MatchDonutWithStrings {...props} strings={props.strings} />;
  }
  if (props.score == null || !Number.isFinite(props.score)) {
    return null;
  }
  return <MatchDonutWithIntl {...props} score={props.score} />;
};

interface MatchDonutWithLoginPromptProps {
  score: number | null; // null if user not logged in
  size?: number;
  className?: string;
  onLoginClick?: () => void;
  breakdown?: MatchScoreBreakdown;
}

/**
 * Match Donut with Login Prompt
 * Shows the match donut if user is logged in, otherwise shows an empty donut with "?" in the center that can be clicked to login.
 */
export const MatchDonutWithLoginPrompt: React.FC<MatchDonutWithLoginPromptProps> = ({
  score,
  size = 64,
  className = '',
  onLoginClick,
  breakdown,
}) => {
  const intl = useIntl();

  if (score === null) {
    // Show empty donut with "?" in center when not logged in (circle from /icons/match-donut-empty.svg)
    const loginTooltip = (
      <div className="text-sm">
        <div className="font-semibold mb-1">
          <FormattedMessage
            id="matchDonut.loginPrompt.title"
            defaultMessage="Login to see your match score"
            description="Title for login prompt tooltip"
          />
        </div>
        <div className="text-xs text-gray-300 dark:text-gray-400">
          <FormattedMessage
            id="matchDonut.loginPrompt.description"
            defaultMessage="Create a profile and add your current job details to see how well jobs match your preferences"
            description="Description for login prompt tooltip"
          />
        </div>
      </div>
    );
    
    const loginButton = (
      <button
        onClick={onLoginClick}
        className={`relative inline-flex items-center justify-center flex-shrink-0 cursor-pointer transition-all hover:scale-105 active:scale-95 ${className}`}
        style={{ width: size, height: size }}
        aria-label={intl.formatMessage({
          id: 'matchDonut.loginPromptAria',
          defaultMessage: 'Log in to see your match score',
          description: 'Accessibility label for login prompt',
        })}
      >
        <span
          className="icon-mask transform -rotate-90 text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500 transition-colors flex-shrink-0"
          style={{
            width: size,
            height: size,
            ['--icon-url' as string]: 'url(/icons/match-donut-empty.svg)',
          }}
          aria-hidden
        />
        {/* Question mark in center with lock icon indicator */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ fontSize: `${size * 0.25}px` }}
        >
          <span className="font-bold text-gray-400 dark:text-gray-500">
            ?
          </span>
        </div>
        {/* Small lock icon in corner to indicate login required */}
        <div
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center"
          style={{ fontSize: '8px' }}
        >
          <span className="text-white">🔒</span>
        </div>
      </button>
    );
    
    return (
      <LazyTooltip content={loginTooltip} position="top">
        {loginButton}
      </LazyTooltip>
    );
  }

  return <MatchDonut score={score} size={size} className={className} breakdown={breakdown} />;
};
